/**
 * AIShield Customer Authentication Screen (UI Batch 1 Redesign)
 * Provides clean, accessible Sign In and Create Account screens.
 * Designed with a centered, rounded dark card, prominent blue action button,
 * clear typography, and seamless mode switching.
 */

import React, { useState, useCallback } from "react";
import { Shield, Eye, EyeOff, Loader2, AlertCircle, CheckCircle2, ArrowRight } from "lucide-react";
import { useAuth } from "../../hooks/useAuth.tsx";
import { ApiClientError } from "../../services/apiClient.ts";

export interface AuthScreenProps {
  onSwitchToDemo?: () => void;
  initialMode?: "login" | "register";
}

export const AuthScreen: React.FC<AuthScreenProps> = ({
  onSwitchToDemo,
  initialMode = "login",
}) => {
  const { login, register } = useAuth();

  const [mode, setMode] = useState<"login" | "register">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Validation & Submission States
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmailFormat = (val: string): boolean => {
    const trimmed = val.trim();
    return trimmed.length > 3 && trimmed.includes("@") && trimmed.includes(".");
  };

  const handleEmailBlur = () => {
    if (email && !validateEmailFormat(email)) {
      setEmailError("Please enter a valid email address.");
    } else {
      setEmailError(null);
    }
  };

  const handlePasswordBlur = () => {
    if (mode === "register" && password && password.length < 8) {
      setPasswordError("Password must be at least 8 characters long.");
    } else {
      setPasswordError(null);
    }
  };

  const handleSwitchMode = (newMode: "login" | "register") => {
    setMode(newMode);
    setServerError(null);
    setSuccessMessage(null);
    setEmailError(null);
    setPasswordError(null);
    setPassword("");
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setServerError(null);
      setSuccessMessage(null);

      // Client-side validations
      const cleanEmail = email.trim();
      if (!cleanEmail) {
        setEmailError("Email address is required.");
        return;
      }
      if (!validateEmailFormat(cleanEmail)) {
        setEmailError("Please enter a valid email address.");
        return;
      }
      setEmailError(null);

      if (!password) {
        setPasswordError("Password is required.");
        return;
      }
      if (mode === "register" && password.length < 8) {
        setPasswordError("Password must be at least 8 characters long.");
        return;
      }
      setPasswordError(null);

      setIsSubmitting(true);
      try {
        if (mode === "login") {
          await login({ email: cleanEmail, password });
          // Successful login updates AuthContext status to "authenticated"
        } else {
          // Explicit registration contract: account is created, but no session is issued.
          await register({ email: cleanEmail, password });
          setSuccessMessage(
            "Account created successfully. Please sign in with your email and password."
          );
          setMode("login");
          setPassword("");
        }
      } catch (err: unknown) {
        let msg = "An unexpected error occurred. Please try again.";
        if (err instanceof ApiClientError) {
          msg = err.message;
        } else if (err instanceof Error) {
          msg = err.message;
        }
        setServerError(msg);
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, password, mode, login, register]
  );

  return (
    <div className="min-h-screen bg-canvas flex flex-col justify-center items-center py-10 px-4 font-sans text-foreground">
      <div className="w-full max-w-[430px]">
        {/* Brand Header */}
        <div className="flex flex-col items-center mb-6 text-center">
          <div className="w-11 h-11 rounded-2xl bg-brand/10 text-brand border border-brand/20 flex items-center justify-center mb-3 shadow-sm">
            <Shield className="w-5 h-5 text-brand" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            AIShield Console
          </h1>
          <p className="text-sm text-foreground-muted mt-1">
            Manage your website’s protection.
          </p>
        </div>

        {/* Authentication Card */}
        <div className="bg-panel border border-border/80 rounded-[22px] p-7 sm:p-8 shadow-2xl shadow-black/40">
          {/* Card Title & Supporting Text */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold tracking-tight text-foreground">
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h2>
            <p className="text-sm text-foreground-muted mt-1.5">
              {mode === "login"
                ? "Sign in to your AIShield account."
                : "Start managing your website’s protection."}
            </p>
          </div>

          {/* Success Banner (e.g. after registration) */}
          {successMessage && (
            <div
              role="status"
              className="mb-5 p-3.5 rounded-[12px] bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm flex items-start gap-2.5"
            >
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-400" />
              <div className="leading-snug">{successMessage}</div>
            </div>
          )}

          {/* Server Error Banner */}
          {serverError && (
            <div
              role="alert"
              className="mb-5 p-3.5 rounded-[12px] bg-verdict-block/10 border border-verdict-block/30 text-verdict-block text-sm flex items-start gap-2.5"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="leading-snug">{serverError}</div>
            </div>
          )}

          {/* Form */}
          <form
            id="panel-auth"
            onSubmit={handleSubmit}
            noValidate
            className="space-y-4"
          >
            {/* Email Field */}
            <div>
              <label
                htmlFor="auth-email-input"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                Email address
              </label>
              <input
                id="auth-email-input"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                onBlur={handleEmailBlur}
                disabled={isSubmitting}
                placeholder="name@company.com"
                aria-invalid={emailError !== null}
                aria-describedby={emailError ? "email-error-desc" : undefined}
                className={`w-full h-12 px-3.5 text-sm rounded-[12px] bg-panel-subtle border text-foreground placeholder:text-foreground-dim/60 transition-colors duration-150 motion-reduce:transition-none focus:outline-none focus:ring-2 ${
                  emailError
                    ? "border-verdict-block focus:border-verdict-block focus:ring-verdict-block/30"
                    : "border-border hover:border-border-strong focus:border-brand focus:ring-brand/30"
                }`}
              />
              {emailError && (
                <p id="email-error-desc" className="text-xs text-verdict-block mt-1.5">
                  {emailError}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="auth-password-input"
                  className="block text-sm font-medium text-foreground"
                >
                  Password
                </label>
                {mode === "register" && (
                  <span className="text-xs text-foreground-muted">
                    Min. 8 characters
                  </span>
                )}
              </div>
              <div className="relative">
                <input
                  id="auth-password-input"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError) setPasswordError(null);
                  }}
                  onBlur={handlePasswordBlur}
                  disabled={isSubmitting}
                  placeholder={mode === "register" ? "••••••••••••" : "••••••••"}
                  aria-invalid={passwordError !== null}
                  aria-describedby={passwordError ? "password-error-desc" : undefined}
                  className={`w-full h-12 px-3.5 pr-11 text-sm rounded-[12px] bg-panel-subtle border text-foreground placeholder:text-foreground-dim/60 transition-colors duration-150 motion-reduce:transition-none focus:outline-none focus:ring-2 ${
                    passwordError
                      ? "border-verdict-block focus:border-verdict-block focus:ring-verdict-block/30"
                      : "border-border hover:border-border-strong focus:border-brand focus:ring-brand/30"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-dim hover:text-foreground transition-colors p-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {passwordError && (
                <p id="password-error-desc" className="text-xs text-verdict-block mt-1.5">
                  {passwordError}
                </p>
              )}
            </div>

            {/* Primary Action Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting || !email.trim() || !password}
                className="w-full h-12 flex items-center justify-center text-sm font-semibold rounded-[12px] bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white transition-all duration-150 motion-reduce:transition-none disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-panel shadow-sm"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin motion-reduce:animate-none" />
                    {mode === "login" ? "Signing in…" : "Creating account…"}
                  </>
                ) : mode === "login" ? (
                  "Sign in"
                ) : (
                  "Create account"
                )}
              </button>
            </div>
          </form>

          {/* Mode Switch Helper Link */}
          <div className="mt-6 pt-1 text-center text-sm text-foreground-muted">
            {mode === "login" ? (
              <span>
                Don’t have an account?{" "}
                <button
                  type="button"
                  onClick={() => handleSwitchMode("register")}
                  className="text-brand hover:text-brand-hover font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand rounded"
                >
                  Create account
                </button>
              </span>
            ) : (
              <span>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => handleSwitchMode("login")}
                  className="text-brand hover:text-brand-hover font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand rounded"
                >
                  Sign in
                </button>
              </span>
            )}
          </div>
        </div>

        {/* Local Demo Navigation Helper (Development Only) */}
        {import.meta.env.DEV && onSwitchToDemo && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={onSwitchToDemo}
              className="inline-flex items-center gap-1.5 text-xs text-foreground-muted hover:text-foreground transition-colors py-1.5 px-3 rounded-lg border border-border bg-panel-subtle hover:bg-panel"
            >
              <span>Explore Local ShieldStore Demo Console</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
